<script setup>
import { computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import SeoCopy from '@/Components/Public/SeoCopy.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import EmptyResults from '@/Components/Public/EmptyResults.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  groups:    { type: Array,  default: () => [] },
  packages:  { type: Array,  default: () => [] },
  base_path: { type: String, default: '/transfers' }
})

// Packages have no own base_path prop here — derive it from the transfer
// base_path (already correctly locale/_p-prefixed by the controller, see
// Public::TransfersController) rather than hardcoding "/packages", which
// silently dropped the "/_p" dev-preview prefix and any locale prefix
// (e.g. "/_p/ru/transfers" -> "/_p/ru/packages", not "/packages").
const packagesBasePath = computed(() => props.base_path.replace(/\/transfers$/, '/packages'))
</script>

<template>
  <Head>
    <title>{{ t('public.transfers.head_title') }}</title>
    <meta name="description" :content="t('public.transfers.head_description')">
  </Head>

  <BrandHero kind="transfer" :title="t('public.transfers.title')">
    <p class="bhero-lede">{{ t('public.transfers.lede') }}</p>
  </BrandHero>

  <section class="listing">
    <div class="listing__inner">
      <EmptyResults
        v-if="groups.length === 0"
        :title="t('public.transfers.empty_title')"
        :sub="t('public.transfers.empty_sub')"
      />

      <div v-else class="groups">
        <div v-for="g in groups" :key="g.point" class="group">
          <h2 class="group__title">{{ g.label }}</h2>
          <ul class="grid">
            <li v-for="r in g.routes" :key="r.id">
              <ProductCard kind="transfer" :item="r" :href="`${base_path}/${r.id}`" />
            </li>
          </ul>
        </div>
      </div>

      <ProductRail
        v-if="packages.length"
        :title="t('public.transfers.packages_heading')"
        kind="package"
        :items="packages"
        :item-base="packagesBasePath"
      />
    </div>
  </section>

  <SeoCopy section="transfers" />
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }

.listing { --card-accent: var(--warn); background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }

.groups { display: grid; gap: 2.5rem; margin-bottom: 2.75rem; }
.group__title {
  font-family: var(--font-display, var(--display)); font-weight: 800;
  font-size: clamp(1.2rem, 2.4vw, 1.5rem); letter-spacing: -0.01em;
  color: var(--ink); margin: 0 0 1rem;
}

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1.25rem; }
</style>
